import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Tooltip,
  Container,
  Fade,
  Button,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { keyframes } from '@mui/system';
import { INPUT_FONT_SIZE } from '../../utils/global-values';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useGetWalletQuery } from '../../components/services/services';

const IBAN = 'TR650001500158007314854714';
const RECEIVER_NAME = 'Ahlatcı Ödeme ve Elektronik Para Hizmetleri A.Ş.';
// Animation keyframes
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const steps = ['Bilgileri Kopyala', 'Bankada İşlem Yap', 'Hesap Onayı'];

const SendMoneyFromBankToAccount: React.FC = () => {
  const theme = useTheme();
  const [copied, setCopied] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const { data: wallets } = useGetWalletQuery(currentUserState?.companyId, {
    skip: !currentUserState?.companyId,
  });

  const companyWallet = wallets?.find((wallet) => wallet.customerId === currentUserState?.companyId && wallet.currencyId === 1);
  const ACCOUNT_NUMBER = companyWallet?.walletNo ?? '';

  const handleNextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  return (
    <Fade in={true} timeout={600}>
      <Container maxWidth={false} sx={{
        flexGrow: 1,
        padding: {
          xs: '80px 8px 16px 8px',
          sm: '90px 12px 24px 12px',
          md: '110px 32px 40px 32px',
          lg: '110px 40px 40px 40px'
        },
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: '100vh'
      }}>
        {/* Main Dashboard Container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
            }
          }}
        >
          <Box sx={{
            p: {
              xs: 1.5,
              sm: 2.5,
              md: 4
            },
            pb: {
              xs: 1,
              md: 2
            },
            background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
            borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
          }}>

            <Box sx={{ 
              padding: {
                xs: '12px 8px',
                sm: '16px 12px',
                md: '20px 32px',
                lg: '20px 40px'
              }
            }}>
              {/* Hero Section */}
              <Fade in={true} timeout={800}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row'
                    },
                    alignItems: {
                      xs: 'flex-start',
                      sm: 'center'
                    },
                    justifyContent: 'space-between',
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    borderRadius: 2,
                    p: {
                      xs: 2,
                      sm: 2.5,
                      md: 3
                    },
                    mb: {
                      xs: 2,
                      md: 3
                    },
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    gap: {
                      xs: 2,
                      sm: 0
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          mb: 0.5,
                          fontSize: '18px !important'
                        }}
                      >
                        Bankadan Hesaba Para Transferi
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    icon={<InfoIcon sx={{ fontSize: 18 }} />}
                    label="Güvenli Transfer"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      mt: {
                        xs: 1,
                        sm: 0
                      }
                    }}
                  />
                </Box>
              </Fade>


              <Grid container spacing={{
                xs: 2,
                sm: 3,
                md: 4
              }} sx={{
                width: '100%',
                margin: 0
              }}>
                {/* Instructions Card */}
                <Grid item xs={12} sx={{ width: '100%' }}>
                  <Fade in={true} timeout={1200}>
                    <Card
                      elevation={8}
                      sx={{
                        borderRadius: { xs: 2, sm: 3, md: 4 },
                        background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        position: 'relative',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        mb: { xs: 2, sm: 0 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: 4,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          }
                        }}
                      >
                      <Box sx={{ 
                        p: {
                          xs: 2,
                          sm: 2.5,
                          md: 3
                        }
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: {
                            xs: 2,
                            md: 3
                          }
                        }}>
                          <AccountBalanceIcon sx={{
                            color: theme.palette.primary.main, 
                            mr: {
                              xs: 1.5,
                              md: 2
                            }, 
                            fontSize: {
                              xs: 24,
                              md: 32
                            }
                          }} />
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }}
                          >
                            İşlem Rehberi
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          mb: {
                            xs: 2,
                            md: 3
                          }
                        }}>
                          <Typography variant="h6" color="primary" fontWeight={700} sx={{ 
                            mb: {
                              xs: 1.5,
                              md: 2
                            }
                          }}>
                            📋 Yapılması Gerekenler:
                          </Typography>
                          <Box sx={{ 
                            pl: {
                              xs: 1,
                              md: 2
                            }
                          }}>
                            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                              Bankanızın para transferi ekranında aşağıdaki bilgileri giriniz:
                            </Typography>

                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: {
                                xs: 1.5,
                                md: 2
                              }
                            }}>
                              <Box component="form" sx={{ width: '100%' }}>
                          <Grid container spacing={{
                            xs: 2,
                            md: 3
                          }}>
                            <Grid item xs={12}>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
                                  💳 IBAN Numarası
                                </Typography>
                              </Box>
                              <TextField
                                value={IBAN}
                                fullWidth
                                disabled
                                variant="outlined"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip title={copied === IBAN ? 'Kopyalandı!' : 'IBAN\'ı Kopyala'}>
                                        <IconButton
                                          onClick={() => navigator.clipboard.writeText(IBAN)}
                                          sx={{
                                            bgcolor: theme.palette.primary.main + '20',
                                            '&:hover': {
                                              bgcolor: theme.palette.primary.main + '30',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                          }}
                                        >
                                          <ContentCopyIcon color="primary" />
                                        </IconButton>
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    bgcolor: 'background.paper',
                                    fontFamily: 'monospace',
                                    '&:hover': {
                                      boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                                    },
                                    '& fieldset': {
                                      borderColor: theme.palette.primary.main + '40',
                                      borderWidth: 2,
                                    },
                                  },
                                  '& .MuiInputBase-input.Mui-disabled': {
                                    color: theme.palette.text.primary,
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    fontWeight: 400,
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="secondary" fontWeight={700} sx={{ mb: 1 }}>
                                  👤 Alıcı Adı
                                </Typography>
                              </Box>
                              <TextField
                                value={RECEIVER_NAME}
                                fullWidth
                                disabled
                                variant="outlined"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip title={copied === RECEIVER_NAME ? 'Kopyalandı!' : 'Alıcı Adını Kopyala'}>
                                        <IconButton
                                          onClick={() => navigator.clipboard.writeText(RECEIVER_NAME)}
                                          sx={{
                                            bgcolor: theme.palette.primary.main + '20',
                                            '&:hover': {
                                              bgcolor: theme.palette.primary.main + '30',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                          }}
                                        >
                                          <ContentCopyIcon color="primary" />
                                        </IconButton>
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                      boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                                    },
                                    '& fieldset': {
                                      borderColor: theme.palette.primary.main + '40',
                                      borderWidth: 2,
                                    },
                                  },
                                  '& .MuiInputBase-input.Mui-disabled': {
                                    color: theme.palette.text.primary,
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    fontWeight: 400,
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="error" fontWeight={700} sx={{ mb: 1 }}>
                                  📝 Açıklama (Hesap Numaranız)
                                </Typography>
                              </Box>
                              <TextField
                                value={ACCOUNT_NUMBER}
                                fullWidth
                                disabled
                                variant="outlined"
                                     InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip title={copied === ACCOUNT_NUMBER ? 'Kopyalandı!' : 'Hesap Numarası Kopyala'}>
                                        <IconButton
                                          onClick={() => navigator.clipboard.writeText(ACCOUNT_NUMBER)}
                                          sx={{
                                            bgcolor: theme.palette.primary.main + '20',
                                            '&:hover': {
                                              bgcolor: theme.palette.primary.main + '30',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                          }}
                                        >
                                          <ContentCopyIcon color="primary" />
                                        </IconButton>
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                                 sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                      boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                                    },
                                    '& fieldset': {
                                      borderColor: theme.palette.primary.main + '40',
                                      borderWidth: 2,
                                    },
                                  },
                                  '& .MuiInputBase-input.Mui-disabled': {
                                    color: theme.palette.text.primary,
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    fontWeight: 400,
                                  },
                                }}
                              />

                            </Grid>

                          </Grid>
                        </Box>
                  
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Fade>
                </Grid>

              </Grid>

              {/* Progress Indicator */}
              {isAnimating && (
                <Fade in={isAnimating}>
                  <Box sx={{ 
                    position: 'fixed', 
                    bottom: {
                      xs: 16,
                      md: 20
                    }, 
                    right: {
                      xs: 16,
                      md: 20
                    }, 
                    zIndex: 1300 
                  }}>
                    <Card
                      elevation={8}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                        color: 'white'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon />
                        <Typography variant="body2" fontWeight={600}>
                          Başarıyla kopyalandı!
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Fade>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Fade >

  );
};

export default SendMoneyFromBankToAccount;
